import React, { useEffect, useState } from "react";
import { Activity, BadgeIndianRupee, Bike, CloudRain, IndianRupee, PackageCheck, ReceiptText, RotateCcw, ShoppingCart, Tags, TrendingUp, Truck, UserCheck, UserMinus, UserPlus, Users, XCircle } from "lucide-react";
import { ReportsService } from "../../../services/reports.service";
import { ReportCard, ReportPanel } from "./components/ReportCards";
import DataTable from "./components/DataTable";
import TrendBars from "./components/TrendBars";
import { money, n } from "./components/reportFormat";

export default function OverviewReports({ filters }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    ReportsService.summary(filters)
      .then((r) => alive && setSummary(r.data))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [filters]);

  const k = summary?.kpis || {};
  const quickRows = [{
    revenue: k.revenue,
    sales_amount: k.sales_amount,
    purchase_cost: k.purchase_cost,
    actual_profit: k.actual_profit,
    completed_orders: k.completed_orders ?? k.delivered_orders,
    refunded_orders: k.refunded_orders,
    refund_amount: k.refund_amount,
    new_users: k.new_users,
    active_users: k.active_users,
    active_drivers: k.active_drivers,
    no_response_tasks: k.no_response_tasks,
    total_delivery_fee: k.total_delivery_fee,
    total_handling_charge: k.total_handling_charge,
    total_rain_charge: k.total_rain_charge,
    total_surge_charge: k.total_surge_charge,
    surge_orders: k.surge_orders,
    total_small_cart_charge: k.total_small_cart_charge,
    small_cart_orders: k.small_cart_orders,
    pay_on_delivery_orders: k.pay_on_delivery_orders,
    pay_on_delivery_pending_orders: k.pay_on_delivery_pending_orders,
    qr_created_orders: k.qr_created_orders,
  }];

  return (
    <div className="space-y-5">
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <ReportCard icon={IndianRupee} title="Revenue" value={money(k.revenue)} />
        <ReportCard icon={BadgeIndianRupee} title="Purchase Cost" value={money(k.purchase_cost)} />
        <ReportCard icon={Tags} title="Actual Profit" value={money(k.actual_profit)} />
        <ReportCard icon={ShoppingCart} title="Completed Orders" value={n(k.completed_orders ?? k.delivered_orders)} />
        <ReportCard icon={RotateCcw} title="Refunded Orders" value={n(k.refunded_orders)} />
        <ReportCard icon={BadgeIndianRupee} title="Refund Amount" value={money(k.refund_amount)} />
        <ReportCard icon={UserPlus} title="New Users" value={n(k.new_users)} />
        <ReportCard icon={UserCheck} title="Active Users" value={n(k.active_users)} />
        <ReportCard icon={Bike} title="Active Drivers" value={n(k.active_drivers)} />
        <ReportCard icon={UserMinus} title="Inactive Users" value={n(k.inactive_users)} />
        <ReportCard icon={XCircle} title="Inactive Drivers" value={n(k.inactive_drivers)} />
        <ReportCard icon={Activity} title="App Open Users" value={n(k.app_open_users)} />
        <ReportCard icon={PackageCheck} title="Driver Opens" value={n(k.driver_app_open)} />
        <ReportCard icon={Users} title="No Response Tasks" value={n(k.no_response_tasks)} />
        <ReportCard icon={Truck} title="Delivery Fee" value={money(k.total_delivery_fee)} />
        <ReportCard icon={ReceiptText} title="Handling Charge" value={money(k.total_handling_charge)} />
        <ReportCard icon={CloudRain} title="Rain Charge" value={money(k.total_rain_charge)} />
        <ReportCard icon={TrendingUp} title="Surge Charge" value={money(k.total_surge_charge)} hint={`${n(k.surge_orders)} orders`} />
        <ReportCard icon={ShoppingCart} title="Small Cart Charge" value={money(k.total_small_cart_charge)} hint={`${n(k.small_cart_orders)} orders`} />
        <ReportCard icon={ReceiptText} title="Pay On Delivery" value={n(k.pay_on_delivery_orders)} hint={`${n(k.pay_on_delivery_pending_orders)} pending`} />
      </div>

      {loading ? <div className="py-14 text-center font-bold text-[#d86a78]">Loading overview...</div> : (
        <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,0.65fr)]">
  <div className="min-w-0 overflow-hidden">
    <TrendBars
      rows={summary?.trend || []}
      field="actual_profit"
      label="Actual profit trend"
      subtitle="Profit is calculated from item selling amount minus purchase cost"
    />
  </div>

  <div className="min-w-0 overflow-hidden">
    <ReportPanel title="Quick Summary" subtitle="Extra totals from summary API">
      <div className="w-full overflow-x-auto">
        <DataTable
          rows={quickRows}
          columns={[
            "revenue",
            "sales_amount",
            "purchase_cost",
            "actual_profit",
            "completed_orders",
            "refunded_orders",
            "refund_amount",
            "new_users",
            "active_users",
            "active_drivers",
            "no_response_tasks",
            "total_delivery_fee",
            "total_handling_charge",
            "total_rain_charge",
            "total_surge_charge",
            "surge_orders",
            "total_small_cart_charge",
            "small_cart_orders",
            "pay_on_delivery_orders",
            "pay_on_delivery_pending_orders",
            "qr_created_orders",
          ]}
          maxHeight="max-h-[260px]"
        />
      </div>
    </ReportPanel>
  </div>
</div>
      )}
    </div>
  );
}
